/**
 * The flight scene's half of ENHANCED SIMULATION's ships: the hulls, the rig
 * they swing, and the two beams each one throws after dark.
 *
 * A sibling of `scene-searchlights.ts` in every respect — the host is described
 * structurally rather than by importing `SceneState`, the pool is built
 * whatever the sortie and gated on what is *fed* to it, and the model is
 * fetched only when there is something to draw with it. `flight-scene.ts` keeps
 * one field and one constructor call; `scene-draw.ts` makes the frame-loop call.
 *
 * ## Nothing here decides anything
 *
 * Every angle this module writes was decided by the sim. A ship's pose is her
 * hull's `EncounterTarget` position and orientation; her mountings' bearings are
 * `EncounterState.shipAims`, which `ship-pass.ts` produced from the same
 * acquisition that fired the guns; her searchlights track whatever her own
 * `FlakBattery`'s slots are laid on, which is `searchlights.ts`'s rule applied
 * to a battery that floats. The one thing this file adds is **rate limiting** —
 * a mounting swings toward its bearing rather than snapping to it — and that is
 * presentation: it changes no hashed state and no peer has to agree about it.
 *
 * ## The main gun's slew rate is the picture
 *
 * A 3-pounder on a high-angle mounting and a Maxim on a pintle are swung by
 * hand and follow an aeroplane; a 4.7 in in a training gunhouse is worked by a
 * crew and a handwheel. `MOUNT_SLEW_DEG_PER_S` gives the first two the
 * searchlight's own 40°/s and the main gun a tenth of it, which is what makes
 * the turret read as *heavy* rather than as broken — it is following, and it
 * will never get there.
 *
 * ## Three things this file adds that the sim does not know about (2026-09-01)
 *
 * All three are pictures read off her **motion output**, all three are
 * `apps/web` side, and none of them can move a bit of hashed state — which is
 * the constraint ENHANCED SIMULATION is judged on (`ship-sim.md` §1).
 *
 * - **Her wake.** `ship-wake.ts` builds it out of where she *was*, which
 *   `shipMotionAt` will answer for any past instant because both a patrol and a
 *   sinking are closed-form in the clock. So there is no emitter and no history:
 *   the wake on the first frame after a load is the one she has been leaving all
 *   along, and the killcam's `dt = 0` redraw produces the same one.
 * - **Her plume.** `ship-smoke.ts`, the same construction one term richer — the
 *   smoke rises as well as staying where it was made. It doubles as her damage
 *   smoke, which is the same picture with a longer, wider, sootier walk.
 * - **Her heel.** A ship in a turn leans **outward**, which is the opposite of
 *   an aeroplane and of anything else this renderer banks: the rudder's side
 *   force acts near the waterline while her weight acts well above it, so the
 *   couple lays her away from the turn's centre. `HEEL_LEVER_RATIO` is the one
 *   number in it that is ours. It takes **her own speed**, so as her way comes
 *   off her it goes to nothing on its own and the flooding list underneath it is
 *   all that is left — no branch, because `tan φ ∝ v²`.
 */

import * as THREE from "three/webgpu";
import {
  SHIP_LIGHT_OFFSETS_M,
  SHIP_MAIN_MOUNT_INDEX,
  SHIP_MAX_COUNT,
  SHIP_MOUNTS,
  SHIP_SPEED_MPS,
  patrolCurvatureAtTime,
  shipDistress,
  shipFightsOn,
  shipMotionAt,
  shipSinkElapsedS,
  type EncounterState,
  type ShipAim,
  type ShipSpec,
  type TimeOfDay,
} from "@red-opus/engine";
import { SearchlightBeams } from "../three/searchlight-beams.ts";
import { loadShipFleet, type ShipFleet, type ShipModel } from "../three/ship-mounts.ts";
import { ShipSmokeRibbons } from "../three/ship-smoke-ribbons.ts";
import { ShipWakeRibbons } from "../three/ship-wake-ribbons.ts";
import { isNight } from "./searchlights.ts";
import { LIGHTS_PER_SHIP, pushShipLights, slewTo, type MountPose } from "./ship-lights.ts";
import type { EncounterRunner } from "./sim-runner.ts";

/** How fast each mounting comes round, degrees a second. See the module note on why the last one is different. */
const MOUNT_SLEW_DEG_PER_S: readonly number[] = SHIP_MOUNTS.map((_unused, index) =>
  index === SHIP_MAIN_MOUNT_INDEX ? 4 : 40,
);

/** Where a mounting rests when it has nothing to look at: on the beam, level. */
const REST_AZIMUTH_RAD = Math.PI / 2;
const REST_ELEVATION_RAD = 0;

/** Metres per second squared. Only the heel uses it. */
const GRAVITY_MPS2 = 9.81;

/**
 * How much she heels in a turn, as the ratio of the heeling lever to her
 * metacentric height. **OURS — stated, not measured.**
 *
 * The steady heel of a turning ship is `tan φ = (v²/gR) · (BG / GM)`, where the
 * numerator is the centripetal acceleration in gravities and the fraction is the
 * distance between her centre of gravity and the line the water's side force
 * acts along, over her metacentric height. Neither of those two is published for
 * an Arabis, and nothing in Red Baron's data has ever contained a stability
 * figure, so **2** is a small-warship proportion stated plainly at the fidelity
 * this port draws at: a couple of metres of lever over a metre or so of `GM`.
 *
 * What it buys, and the reason it is not larger: at
 * `SHIP_CRUISE_TURN_RADIUS_M` it lays her over **2.7°**, which reads at any
 * range her masts are visible from and is invisible in a still. A destroyer
 * heeling hard over does five to ten; an escort altering course on patrol does
 * about this.
 */
const HEEL_LEVER_RATIO = 2;

/**
 * The heel a ship makes when her track is curving `curvaturePerM` at
 * `speedMps` — **outward**, which is what distinguishes a ship from every other
 * thing this renderer banks.
 *
 * An aeroplane rolls *into* a turn because its lift is what turns it. A ship's
 * turning force comes from the water on her rudder and her hull, which acts near
 * the waterline, while her mass is centred well above it — so the couple lays
 * her *away* from the centre of the turn. (There is a brief inward heel at the
 * instant the helm goes over, before the ship starts to swing; the port does not
 * model it, and at a cruising helm nobody would see it.)
 */
export function shipHeelRad(curvaturePerM: number, speedMps: number): number {
  return Math.atan((HEEL_LEVER_RATIO * speedMps * speedMps * curvaturePerM) / GRAVITY_MPS2);
}

/** One drawn ship's whole per-frame state. */
interface ShipView {
  readonly model: ShipModel;
  readonly mounts: MountPose[];
  readonly lights: MountPose[];
  /** The ship this view is showing, or `null` while the fleet is longer than the sortie's. */
  spec: ShipSpec | null;
}

export interface Ships {
  /** Add this to the scene once; every hull and every beam draws inside it. */
  readonly group: THREE.Group;
  /** Whether this mission's briefed hour lights the projectors. Settled once, like `Searchlights.night`. */
  readonly night: boolean;
  /**
   * Poses this frame's ships. `enabled` false publishes nothing at all, which
   * is the whole of the ENHANCED SIMULATION off case — though a sortie flown
   * with the switch off has an empty ship list anyway, so the flag is a second
   * lock on a door that is already shut.
   *
   * `dt` of 0 is meaningful: the kill camera re-draws one instant through a
   * second lens, and a mounting must not creep because it was looked at twice.
   */
  update(dt: number, runner: EncounterRunner, enabled: boolean): void;
  /** Hulls drawn by the last `update` — what the tests and the draw count read. */
  readonly liveCount: number;
  drawCallCount(): number;
  dispose(): void;
}

/** The heading a hull's orientation quaternion carries, without pulling a whole quaternion library in. */
function headingOf(orientation: { x: number; y: number; z: number; w: number }): number {
  const { x, y, z, w } = orientation;
  return Math.atan2(2 * (w * y + x * z), 1 - 2 * (y * y + z * z));
}

class ShipsImpl implements Ships {
  readonly group = new THREE.Group();
  readonly night: boolean;
  private readonly beams = new SearchlightBeams(SHIP_MAX_COUNT * LIGHTS_PER_SHIP);
  private readonly wakes = new ShipWakeRibbons(SHIP_MAX_COUNT);
  private readonly smoke = new ShipSmokeRibbons(SHIP_MAX_COUNT);
  private fleet: ShipFleet | undefined;
  private views: ShipView[] = [];
  private drawn = 0;
  private disposed = false;

  constructor(timeOfDay: TimeOfDay | undefined) {
    this.night = isNight(timeOfDay);
    this.group.add(this.beams.mesh);
    // The wake needs no model, so it is added here rather than in `adoptFleet`:
    // it is built out of poses and would draw correctly even if the `.glb` never
    // arrived. In practice it never gets the chance — nothing is pushed to it
    // for a ship whose hull is not on screen.
    this.group.add(this.wakes.mesh);
    this.group.add(this.smoke.mesh);
    void this.adoptFleet();
  }

  private async adoptFleet(): Promise<void> {
    const fleet = await loadShipFleet(SHIP_MAX_COUNT);
    if (fleet === undefined) return;
    // The scene may have been torn down while the fetch was in flight — a
    // sortie abandoned at the loading screen — and adding to a disposed group
    // would leak what it just built.
    if (this.disposed) {
      fleet.dispose();
      return;
    }
    this.fleet = fleet;
    this.views = fleet.ships.map((model) => ({
      model,
      mounts: SHIP_MOUNTS.map(() => ({ azimuthRad: REST_AZIMUTH_RAD, elevationRad: REST_ELEVATION_RAD })),
      lights: SHIP_LIGHT_OFFSETS_M.map(() => ({ azimuthRad: 0, elevationRad: Math.PI / 4 })),
      spec: null,
    }));
    for (const view of this.views) {
      view.model.root.visible = false;
      this.group.add(view.model.root);
    }
  }

  update(dt: number, runner: EncounterRunner, enabled: boolean): void {
    this.beams.begin();
    this.wakes.begin();
    this.smoke.begin();
    this.drawn = 0;
    const state = runner.encounter();
    const ships = enabled ? state.ships : [];
    for (const [index, view] of this.views.entries()) {
      const ship = ships[index];
      view.spec = ship ?? null;
      if (!ship) {
        view.model.root.visible = false;
        continue;
      }
      this.drawShip(view, ship, state, dt);
      this.drawn += 1;
    }
    this.beams.end();
    this.wakes.end();
    this.smoke.end();
  }

  /** One ship: her hull on the sea, her five mountings, her plume, and her lights after dark. */
  private drawShip(view: ShipView, ship: ShipSpec, state: EncounterState, dt: number): void {
    const hull = state.targets.find((target) => target.id === ship.id);
    // A hull that is `destroyed` has gone under. Nothing of her is drawn from
    // that frame — not the ship, not the wake, not the plume — and the last
    // pose anything saw was `SHIP_SINK_DEPTH_M` down, which is past her own
    // masthead, so there is no pop.
    if (!hull || hull.destroyed) {
      view.model.root.visible = false;
      return;
    }
    if (!view.model.root.visible) {
      // Both ensigns arrive visible from glTF (ship-design.md §5), so this is
      // the first act of drawing a ship and not an optimisation.
      view.model.setEnsign(ship.alliance);
      view.model.root.visible = true;
    }
    // Everything below is a function of her **motion**, which is the sim's own
    // answer for where she is and what she is doing — steaming her circuit or
    // going down on her last course. One call, both regimes, and it is askable
    // for any past instant, which is what the wake and the plume need.
    const sinkElapsedS = shipSinkElapsedS(hull);
    const motion = shipMotionAt(ship.patrol, state.timeS, sinkElapsedS);
    const poseAt = (ageS: number) => shipMotionAt(ship.patrol, state.timeS - ageS, sinkElapsedS - ageS);
    // Her turning heel is read off the curve she is on rather than differenced
    // from last frame's heading: `dt` is zero on a killcam redraw, and a finite
    // difference would either divide by it or quietly hold the last answer. It
    // takes her *own* speed, so it dies with her way as she loses it — no
    // branch, because `tan φ ∝ v²` and her `v` is going to zero.
    const curvature = patrolCurvatureAtTime(ship.patrol, state.timeS, SHIP_SPEED_MPS);
    view.model.setPose(
      motion.x,
      motion.z,
      headingOf(hull.orientation),
      shipHeelRad(curvature, motion.speedMps) + motion.listRad,
      motion.trimRad,
      motion.sinkM,
    );
    // The wake is a function of where she has been, so it is asked for her past
    // poses directly. Negative time is meaningful and wanted: a sortie that
    // opens with her already under way should open with her wake already behind
    // her, not with a stub that grows for the first minute.
    this.wakes.push(poseAt, motion.speedMps);
    // Her plume, on the same past poses. Unlike the wake it is never declined:
    // a ship stopped dead still has a fire in her.
    this.smoke.push(poseAt, shipDistress(hull));

    const aim = state.shipAims.find((entry: ShipAim) => entry.shipId === ship.id);
    for (const [index, pose] of view.mounts.entries()) {
      const wanted = aim?.mounts[index] ?? null;
      slewTo(
        pose,
        wanted?.azimuthRad ?? REST_AZIMUTH_RAD,
        wanted?.elevationRad ?? REST_ELEVATION_RAD,
        MOUNT_SLEW_DEG_PER_S[index] ?? 40,
        dt,
      );
      view.model.aimMount(index, pose.azimuthRad, pose.elevationRad);
    }

    // Her lights go out with her guns. `shipFightsOn` is the sim's own
    // predicate, the one `ship-pass.ts` silences her Maxims on and pass 0 drops
    // her battery on, so a sinking ship cannot be found sweeping the sky
    // because a third module spelled "she has stopped" differently.
    if (!this.night || !shipFightsOn(hull)) return;
    pushShipLights({
      model: view.model,
      poses: view.lights,
      ship,
      state,
      beams: this.beams,
      headingRad: headingOf(hull.orientation),
      dt,
    });
  }

  get liveCount(): number {
    return this.drawn;
  }

  drawCallCount(): number {
    if (this.drawn === 0) return 0;
    // One per drawn hull — a ship is one geometry with one material — plus the
    // beams' single instanced mesh when anything is lit, plus **one** for every
    // wake in the sortie together and **one** for every plume, each sharing a
    // mesh and an index buffer with the rest of its kind.
    return (
      this.drawn +
      (this.beams.liveCount > 0 ? 1 : 0) +
      (this.wakes.liveCount > 0 ? 1 : 0) +
      (this.smoke.liveCount > 0 ? 1 : 0)
    );
  }

  dispose(): void {
    this.disposed = true;
    this.beams.dispose();
    this.wakes.dispose();
    this.smoke.dispose();
    this.fleet?.dispose();
  }
}

/**
 * Builds the ship pool and adds it to `scene`.
 *
 * **Always built**, like `createSearchlights` and for the same reason: an empty
 * pool costs one hidden `InstancedMesh` and zero draw calls, where building it
 * the first time a sortie deployed a ship would mean a pipeline compile
 * mid-flight. The `.glb` behind it is fetched once, in the background, and its
 * absence is a normal state (`three/ship-mounts.ts`).
 */
export function createShips(scene: THREE.Scene, timeOfDay: TimeOfDay | undefined): Ships {
  const ships = new ShipsImpl(timeOfDay);
  scene.add(ships.group);
  return ships;
}

/** The two fields the frame loop needs, structurally — `scene-draw.ts`'s host, not `SceneState`. */
export interface ShipHost {
  readonly runner: EncounterRunner;
  readonly ships: Ships;
}

export function updateShips(host: ShipHost, dt: number): void {
  host.ships.update(dt, host.runner, true);
}
